import { AsyncPipe, NgIf } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { EBookingPageType, injectEnvironment } from '@cf/core';
import { GoogleAuthComponent, MicrosoftAuthComponent } from '@cf/features/auth/shell';
import { ButtonComponent, LinkComponent } from '@cf/shared/ui/button';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { AvatarComponent } from '@cf/shared/ui/logos';
import { NotificationContainerComponent } from '@cf/shared/ui/notifications';
import { TextComponent, TextTruncateComponent } from '@cf/shared/ui/typography';
import type {
  IAccount,
  ITimeslots,
  SchemaAppEventDetail,
  SchemaMeetingInfo,
} from '@cf/temp/services';
import { NgxTolgeeModule } from '@tolgee/ngx';
import type { Observable } from 'rxjs';
import { scan, takeWhile, timer } from 'rxjs';
import { BookingInformationComponent } from '../../shared/booking-information/booking-information.component';
import { ParticipantListComponent } from '../../shared/participant-list/participant-list.component';

@Component({
  selector: 'cf-booking-confirmation',
  templateUrl: './booking-confirmation.component.html',
  styleUrl: './booking-confirmation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    NotificationContainerComponent,
    NgIf,
    AvatarComponent,
    NgxTolgeeModule,
    TextComponent,
    TextTruncateComponent,
    BookingInformationComponent,
    ParticipantListComponent,
    ButtonComponent,
    LinkComponent,
    AsyncPipe,
    GoogleAuthComponent,
    MicrosoftAuthComponent,
    CfIconDirective,
  ],
})
export class BookingConfirmationComponent implements OnInit {
  private readonly router = inject(Router);

  @Input() heading!: string;
  @Input() description!: string;

  @Input() booking!: SchemaMeetingInfo | SchemaAppEventDetail;
  @Input() selectedSlot!: ITimeslots;
  @Input() selectedTime!: string;
  @Input() timezone!: string;
  @Input() selectedDuration!: string;
  @Input() isPreview!: boolean;
  @Input({ required: true }) eventAddedToCalendar: boolean | undefined;

  @Input() participantsAmount!: number;

  @Input() account!: IAccount;

  @Output() scheduleAnother = new EventEmitter();

  redirectTimer$!: Observable<number>;

  private environment = injectEnvironment();

  get hasRedirect() {
    return (
      'redirect_url' in this.booking &&
      this.booking.redirect_url &&
      this.router.url.startsWith('/b/')
    );
  }

  get redirectUrl() {
    return 'redirect_url' in this.booking ? this.booking.redirect_url : '';
  }

  get scheduleAnotherEventButton() {
    return (
      'schedule_another_event_button' in this.booking && this.booking.schedule_another_event_button
    );
  }

  get showCalfrenzyLogo() {
    return 'show_cal_frenzy_logo' in this.booking && this.booking.show_cal_frenzy_logo;
  }

  get location() {
    return 'location' in this.booking ? this.booking.location : null;
  }

  get title() {
    return 'summary' in this.booking
      ? this.booking.summary
      : 'public_title' in this.booking
        ? this.booking.public_title
        : '';
  }

  get picture() {
    return 'picture' in this.booking ? this.booking.picture : null;
  }

  get withPicture() {
    return 'with_picture' in this.booking && this.booking.with_picture;
  }

  ngOnInit() {
    if (this.hasRedirect) {
      this.redirectTimer$ = timer(0, 1000).pipe(
        scan((ticks) => --ticks, 5),
        takeWhile((v) => v >= 0),
      );

      this.redirectTimer$.subscribe((timer) => {
        if (timer === 0) {
          window.open(this.redirectUrl, '_self');
        }
      });
    }
  }

  reload() {
    this.scheduleAnother.emit();
  }

  signUp() {
    window.open(`${this.environment.appUrl}/auth/sign-up`, '_self');
  }

  protected readonly EMeetingType = EBookingPageType;
}
